import React, { useState } from "react";
import {
    Box, Button, TextField, IconButton, Typography, Paper, Tabs, Tab
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import * as XLSX from "xlsx";

const SPLINE_NAMES = ["preprocessing", "g1", "g2", "g3", "g4"];

/**
 * Editor for the five MicroSlide splines (preprocessing, g1..g4).
 * Each spline is a list of { x, y, y2 } nodes.
 *
 * Props:
 *   splines:    { preprocessing, g1, g2, g3, g4 } where each value is
 *               { nodes: [{x,y,y2}], length } or null.
 *   setSplines: setter
 */
export default function Splines({ splines, setSplines }) {
    const [active, setActive] = useState(0);
    const splineName = SPLINE_NAMES[active];
    const current = splines[splineName] || { nodes: [{ x: "", y: "", y2: "" }], length: 0 };
    const nodes = current.nodes.length ? current.nodes : [{ x: "", y: "", y2: "" }];

    const update = (newNodes) => {
        const filtered = newNodes
            .filter(n => n.x !== "" && n.y !== "")
            .map(n => ({
                x: parseFloat(n.x),
                y: parseFloat(n.y),
                y2: parseFloat(n.y2) || 0
            }));
        setSplines({
            ...splines,
            [splineName]: filtered.length ? { nodes: filtered, length: filtered.length } : null,
            [`${splineName}_draft`]: newNodes // keep raw entries while editing
        });
    };

    const draft = splines[`${splineName}_draft`] || nodes;

    const handleNodeChange = (idx, field, value) => {
        const next = draft.map((n, i) => i === idx ? { ...n, [field]: value } : n);
        update(next);
    };

    const addNode = () => update([...draft, { x: "", y: "", y2: "" }]);
    const removeNode = (idx) => {
        const next = draft.filter((_, i) => i !== idx);
        update(next.length ? next : [{ x: "", y: "", y2: "" }]);
    };

    const handleFile = (file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            let lines;
            const ext = file.name.split(".").pop().toLowerCase();
            if (ext === "xls" || ext === "xlsx") {
                const data = new Uint8Array(evt.target.result);
                const wb = XLSX.read(data, { type: "array" });
                const sheet = wb.Sheets[wb.SheetNames[0]];
                lines = XLSX.utils.sheet_to_json(sheet, { header: 1 });
            } else {
                lines = evt.target.result.split(/\r?\n/).map(l => l.split(/[\t, ]+/));
            }
            const parsed = lines
                .filter(r => r && r.length >= 2 && r[0] !== "" && r[1] !== "")
                .map(r => ({ x: r[0], y: r[1], y2: r[2] || "" }))
                .filter(n => !isNaN(parseFloat(n.x)) && !isNaN(parseFloat(n.y)));
            if (parsed.length) update(parsed);
        };
        if (file.name.match(/\.(xls|xlsx)$/i)) reader.readAsArrayBuffer(file);
        else reader.readAsText(file);
    };

    return (
        <Paper elevation={0} sx={{ border: "none" }}>
            <Tabs
                value={active}
                onChange={(e, v) => setActive(v)}
                sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
            >
                {SPLINE_NAMES.map((n) => (
                    <Tab
                        key={n}
                        label={`${n.toUpperCase()}${splines[n] ? ` (${splines[n].nodes.length})` : ""}`}
                    />
                ))}
            </Tabs>

            <Box display="flex" gap={1} mb={2}>
                <Button variant="contained" component="label" startIcon={<UploadFileIcon />}>
                    Upload {splineName.toUpperCase()}
                    <input
                        type="file"
                        hidden
                        accept=".csv,.txt,.xls,.xlsx"
                        onChange={(e) => handleFile(e.target.files[0])}
                    />
                </Button>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={addNode}>
                    Add Node
                </Button>
            </Box>

            <Box border={1} borderColor="grey.300" borderRadius={1} p={1}>
                <Box display="flex" gap={1} mb={1}>
                    <Typography sx={{ flex: 1, fontWeight: 600 }}>X</Typography>
                    <Typography sx={{ flex: 1, fontWeight: 600 }}>Y</Typography>
                    <Typography sx={{ flex: 1, fontWeight: 600 }}>Y2</Typography>
                    <Box width={40} />
                </Box>

                {draft.map((n, i) => (
                    <Box key={i} display="flex" gap={1} mb={1}>
                        <TextField size="small" sx={{ flex: 1 }} value={n.x}
                            onChange={(e) => handleNodeChange(i, "x", e.target.value)} />
                        <TextField size="small" sx={{ flex: 1 }} value={n.y}
                            onChange={(e) => handleNodeChange(i, "y", e.target.value)} />
                        <TextField size="small" sx={{ flex: 1 }} value={n.y2}
                            onChange={(e) => handleNodeChange(i, "y2", e.target.value)} />
                        <IconButton size="small" onClick={() => removeNode(i)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Box>
                ))}
            </Box>
        </Paper>
    );
}
